import {
  getActivityProducts,
} from "../../services/activityApi";


export class RetailPOSProductRuntimeError
  extends Error {
  constructor(
    message,
    {
      code =
        "RETAIL_POS_PRODUCT_RUNTIME_ERROR",

      cause = null,
    } = {}
  ) {
    super(message);

    this.name =
      "RetailPOSProductRuntimeError";

    this.code =
      code;

    if (cause !== null) {
      this.cause =
        cause;
    }
  }
}


/**
 * Retail POS Product Runtime.
 *
 * Stateless adapter over the authoritative Activity Product
 * collection.
 *
 * Authority:
 *
 *   GET /activities/:activityUuid/products
 *
 * Scope:
 *
 *   companyUuid
 *   activityUuid
 *
 * This adapter does NOT:
 *
 * - own Product state;
 * - persist Product data;
 * - calculate prices;
 * - calculate VAT;
 * - calculate inventory;
 * - create a Product Engine;
 * - reuse Supplier Invoice search.
 */
export function createRetailPOSProductRuntime({
  getProducts =
    getActivityProducts,
} = {}) {
  if (
    typeof getProducts
      !== "function"
  ) {
    throw new TypeError(
      "Retail POS Product Runtime requires getProducts()."
    );
  }


  async function loadProducts(
    context = {}
  ) {
    const scope =
      normalizeScope(
        context
      );

    let response;

    try {
      response =
        await getProducts(
          scope.activityUuid,
          {
            companyUuid:
              scope.companyUuid,
          }
        );
    } catch (cause) {
      throw new RetailPOSProductRuntimeError(
        "Unable to load authoritative Activity Products.",
        {
          code:
            "PRODUCT_COLLECTION_LOAD_FAILED",

          cause,
        }
      );
    }

    const products =
      response?.data?.products
      ?? response?.products
      ?? [];

    if (!Array.isArray(products)) {
      throw new RetailPOSProductRuntimeError(
        "Activity Product authority must return a products array.",
        {
          code:
            "INVALID_PRODUCT_COLLECTION",
        }
      );
    }

    return products;
  }


  async function resolveProductByCode(
    code,
    context = {}
  ) {
    const normalizedCode =
      normalizeRequiredSearchValue(
        code,
        "code"
      );

    const products =
      await loadProducts(
        context
      );

    const matches =
      products.filter(
        (product) =>
          EXACT_CODE_FIELDS.some(
            (field) =>
              normalizeComparable(
                product?.[field]
              )
              === normalizedCode
          )
      );

    if (matches.length === 0) {
      return null;
    }

    if (matches.length > 1) {
      throw new RetailPOSProductRuntimeError(
        "Activity Product code is ambiguous.",
        {
          code:
            "AMBIGUOUS_PRODUCT_CODE",
        }
      );
    }

    return matches[0];
  }


  async function searchProducts(
    query,
    context = {}
  ) {
    const normalizedQuery =
      normalizeRequiredSearchValue(
        query,
        "query"
      );

    const products =
      await loadProducts(
        context
      );

    return products.filter(
      (product) =>
        SEARCH_FIELDS.some(
          (field) =>
            normalizeComparable(
              product?.[field]
            ).includes(
              normalizedQuery
            )
        )
    );
  }


  return Object.freeze({
    loadProducts,
    resolveProductByCode,
    searchProducts,
  });
}


const EXACT_CODE_FIELDS =
  Object.freeze([
    "barcode",
    "sku",
    "uuid",
  ]);


const SEARCH_FIELDS =
  Object.freeze([
    "designation",
    "sku",
    "barcode",
  ]);


function normalizeScope(
  context
) {
  if (
    context === null
    || typeof context
      !== "object"
    || Array.isArray(context)
  ) {
    throw new TypeError(
      "Retail Product scope must be an object."
    );
  }

  return Object.freeze({
    companyUuid:
      normalizeRequiredScopeValue(
        context.companyUuid,
        "companyUuid"
      ),

    activityUuid:
      normalizeRequiredScopeValue(
        context.activityUuid,
        "activityUuid"
      ),
  });
}


function normalizeRequiredScopeValue(
  value,
  field
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new TypeError(
      `Retail Product scope requires ${field}.`
    );
  }

  return value.trim();
}


function normalizeRequiredSearchValue(
  value,
  field
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new TypeError(
      `Retail Product ${field} must be a non-empty string.`
    );
  }

  return normalizeComparable(
    value
  );
}


function normalizeComparable(
  value
) {
  return String(
    value
    ?? ""
  )
    .trim()
    .toLocaleLowerCase();
}
